import type { FC } from "react";
import { useState } from "react";

import type { RawParams } from "@uirouter/react";
import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch, useSelector } from "react-redux";

import { tableActionsCreator } from "app/actions/tableActions";
import { showSuccessToast } from "app/actions/toast_actions";
import {
    DEFAULT_SORT,
    getKeywordsDrilldownTableColumns,
} from "app/pages/keyword-research/website-analysis/pages/landing-pages/OrganicLandingPagesColumnsConfigV2";
import { SWReactTableWrapperWithSelection } from "app/components/React/Table/SWReactTableWrapperWithSelection";
import { KeywordSelection } from "app/components/React/TableSelectionComponents/KeywordSelection";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";
import { getShareValue, getVolumeValue } from "../../../../website-analysis/traffic-sources/search/common/utils";

import { useLandingPagesEndpoints } from "./landingPagesEndpoints";
import { sourceTypeMap } from "app/common/keyword-research/landing-pages/landingPagesTypes";
import { allTrackers } from "app/services/track/track";

interface IDataRecord {
    Clicks: number;
    ClicksChange: number;
    ClicksShare: number;
    LatestKeywordSerpDate: string;
    Position: number;
    PreviousClicks: number;
    PreviousPosition: number;
    KeywordCount: number;
    SerpFeatures: string[];
    Keyword: string;
    Volume: number;
    Cpc: number;
    DesktopClicks: number;
    AverageVolume: number;
}

interface IOrganicLandingPageEnrichedKeywordsTableProps {
    row: any;
    selectedDomain: string;
    sort?: string;
    asc?: boolean;
    keywordTargetState: string;
    getKeywordExpansionColumnsFunction?: typeof getKeywordsDrilldownTableColumns;
}

export const OrganicLandingPageEnrichedKeywordsTable: FC<IOrganicLandingPageEnrichedKeywordsTableProps> = ({
    row,
    selectedDomain,
    sort = DEFAULT_SORT,
    asc = false,
    keywordTargetState,
    getKeywordExpansionColumnsFunction = getKeywordsDrilldownTableColumns,
}) => {
    const dispatch = useDispatch();
    const { params, state } = useCurrentStateAndParams();
    const endpoints = useLandingPagesEndpoints();
    const { to, from, isWindow } = proNavigator.getApiParams();
    const selectedRows = useSelector(getTableSelectedItems("OrganicPagesKeywordsV2_Table"));
    const [sortConfig, setSortConfig] = useState({ sort, sortDirection: asc ? "asc" : "desc" });

    const apiParams = {
        country: params.country,
        webSource: params.webSource,
        includeSubDomains: params.isWWW === "*",
        to,
        from,
        isWindow,
        landingPage: row.Url,
        rowsPerPage: 50,
        key: selectedDomain,
        sort,
        asc,
        sourceType: sourceTypeMap[params.selectedPageTab],
    };

    const transformData = (data: { Data: IDataRecord[] }) => {
        return {
            ...data,
            Data: (data?.Data ?? []).map((record: IDataRecord) => {
                const shareOfDesktop = record.DesktopClicks / record.Clicks;
                return {
                    ...record,
                    ClicksShare: getShareValue(record.ClicksShare),
                    Clicks: getVolumeValue(record.Clicks),
                    Volume: getVolumeValue(record.Volume),
                    AverageVolume: getVolumeValue(record.AverageVolume),
                    SiteSerpFeatures: { [selectedDomain]: record.SerpFeatures },
                    SerpScrapeDate: record.LatestKeywordSerpDate,
                    PositionObj: [
                        {
                            Key: selectedDomain,
                            Value: record.Position,
                        },
                    ],
                    SearchTerm: record.Keyword,
                    WebSourceShare: shareOfDesktop,
                };
            }),
        };
    };

    const changePageCallback = (page: number) => {
        allTrackers.dispatchTrackEvent("Pagination", "switch", `page ${page.toString() || ""}`);
    };

    const clearAllSelectedRows = () => {
        dispatch(tableActionsCreator("OrganicPagesKeywordsV2_Table", "SearchTerm").clearAllSelectedRows());
    };

    const showToast = (href: string, text: string, label: string) => {
        dispatch(
            showSuccessToast(
                getToastItemComponent({
                    text,
                    linkText: label,
                    href,
                    onClick: () =>
                        allTrackers.dispatchTrackEvent(
                            "organic.landing.v2.inner.add.to.keyword.group",
                            "switch",
                            "organic.landing.v2.inner.add.to.keyword.group",
                        ),
                }),
            ),
        );
    };

    const dataParamsAdapter = (currentParams) => {
        const transformedParams = { ...currentParams };
        if (transformedParams.sort === "PositionObj") {
            transformedParams.sort = "Position";
            transformedParams.orderBy = transformedParams.orderBy.replace("PositionObj", "Position");
        }
        return transformedParams;
    };

    const onSort = ({ field, sortDirection }) => {
        setSortConfig({ sort: field, sortDirection });
    };

    const columns = getKeywordExpansionColumnsFunction(
        params,
        sortConfig.sort,
        sortConfig.sortDirection,
        keywordTargetState,
        row.Url,
    );

    return (
        <SWReactTableWrapperWithSelection
            tableSelectionKey="OrganicPagesKeywordsV2_Table"
            tableSelectionProperty="SearchTerm"
            transformData={transformData}
            initialFilters={apiParams}
            serverApi={endpoints.tableDrillDown}
            tableColumns={columns}
            tableOptions={{
                noDataI18n: "organic.landing.pages.inner.table.nodata",
                longLoader: {
                    title: translate("organic.landing.pages.inner.table.loader.title"),
                    subtitle: translate("organic.landing.pages.inner.table.loader.subtitle"),
                },
                aboveHeaderComponents: [
                    <KeywordSelection
                        key="tableSelection"
                        appendTo="body"
                        clearAllSelectedRows={clearAllSelectedRows}
                        notify={true}
                        selectedRows={selectedRows}
                        showToast={showToast}
                    />,
                ],
                metric: "OrganicPagesKeywordsV2_Table",
                customTableClass: "organic-landing-pages-keywords-table",
            }}
            recordsField="Data"
            totalRecordsField="TotalCount"
            onSort={onSort}
            changePageCallback={changePageCallback}
            fetchServerPages={5}
            rowsPerPage={10}
            dataParamsAdapter={dataParamsAdapter}
            pageIndent={5}
        />
    );
};
